import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useEntityQuickFind } from '@/api/rpc/hooks/core/useEntityQuickFind';
import { useDebounce } from '@/hooks/useDebounce';
import { useConfigurableHotkey } from '@/hooks/useConfigurableHotkey';
import { useHotkeyLabel } from '@/hooks/useHotkeyLabel';
import { HOTKEY_IDS } from '@/lib/hotkeys/registry';
import { useQuickSearchPages } from '@/hooks/useQuickSearchPages';
import type { QuickSearchPage } from '@/hooks/quickSearchMatch';
import paths from '@/paths';
import type { EntityQuickFindResultItem } from '@erp/rpc';
import { SystemEntity } from '@erp/shared-types';
import {
  UserIcon,
  FileTextIcon,
  ShoppingCartIcon,
  BuildingsIcon,
  HandshakeIcon,
  MagnifyingGlassIcon,
  CaretDownIcon,
  CaretRightIcon,
  BrowsersIcon,
  GearIcon,
  FileDashedIcon,
  ReceiptIcon,
  TruckIcon,
  BankIcon,
  ChartBarIcon,
  TreeStructureIcon,
  EnvelopeIcon,
  ChatTextIcon,
  ListChecksIcon,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { Spinner } from '@/components/ui/spinner';
import { T, useGT } from 'gt-react';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { HELP_MENU_ORDER, HelpMenuSlot } from '@/components/common/HelpMenu';

interface CategoryConfig {
  heading: string;
  icon: Icon;
  navigateTo: (
    navigate: ReturnType<typeof useNavigate>,
    item: EntityQuickFindResultItem,
  ) => void;
}

const CATEGORY_CONFIG: Partial<Record<SystemEntity, CategoryConfig>> = {
  [SystemEntity.TASK]: {
    heading: 'Tasks',
    icon: ListChecksIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: '/tasks/$taskId',
        params: { taskId: id },
      }),
  },
  [SystemEntity.CORE_COMPANY]: {
    heading: 'Companies',
    icon: BuildingsIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: '/companies/$id',
        params: { id },
      }),
  },
  [SystemEntity.AR_CUSTOMER]: {
    heading: 'Collections Profile',
    icon: UserIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.accountsReceivable.customerProfile,
        params: { customerId: id },
      }),
  },
  [SystemEntity.AR_INVOICE]: {
    heading: 'AR Invoices',
    icon: FileTextIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.accountsReceivable.invoice,
        params: { arInvoiceId: id },
      }),
  },
  [SystemEntity.AR_COLLECTION_THREAD]: {
    heading: 'Collection Threads',
    icon: ChatTextIcon,
    navigateTo: (navigate, item) => {
      // Detail route is nested under the customer; parentId carries it.
      if (!item.parentId) return;
      navigate({
        to: '/accounts-receivable/collections/$customerId/threads/$threadId',
        params: { customerId: item.parentId, threadId: item.id },
      });
    },
  },
  [SystemEntity.AR_REMITTANCE_ADVICE]: {
    heading: 'Remittances',
    icon: ReceiptIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: '/accounts-receivable/remittances/$remittanceId',
        params: { remittanceId: id },
      }),
  },
  [SystemEntity.AR_CASH_RECEIPT]: {
    heading: 'Cash Receipts',
    icon: ReceiptIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: '/accounts-receivable/cash-receipts',
        search: { receiptId: id },
      }),
  },
  [SystemEntity.AR_SALES_ORDER]: {
    heading: 'Sales Orders',
    icon: ShoppingCartIcon,
    // Legacy v2 entity — its detail page lives at /sales-orders-v2 since OMS
    // v3 took over the unversioned path.
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.salesOrderV2,
        params: { salesOrderId: id },
      }),
  },
  [SystemEntity.AR_STAGED_SALES_ORDER]: {
    heading: 'Sales Order Drafts',
    icon: FileDashedIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.salesOrdersV2,
        search: { salesOrderId: id },
      }),
  },
  [SystemEntity.AP_INVOICE]: {
    heading: 'AP Invoices',
    icon: ReceiptIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.accountsPayable.invoice,
        params: { invoiceId: id },
      }),
  },
  [SystemEntity.INTERNAL_PURCHASE_ORDER]: {
    heading: 'Purchase Orders',
    icon: TruckIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.accountsPayable.purchaseOrder,
        params: { purchaseOrderId: id },
      }),
  },
  [SystemEntity.PAYMENT]: {
    heading: 'AP Payments',
    icon: BankIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.accountsPayable.payment,
        params: { paymentId: id },
      }),
  },
  [SystemEntity.AR_SAVED_REPORT]: {
    heading: 'AR Reports',
    icon: ChartBarIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.accountsReceivable.savedReport,
        params: { savedReportId: id },
      }),
  },
  [SystemEntity.WORKFLOW_DEFINITION]: {
    heading: 'Workflows',
    icon: TreeStructureIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.workflows.workflow,
        params: { workflowId: id },
      }),
  },
  [SystemEntity.INBOUND_EMAIL]: {
    heading: 'Inbound Emails',
    icon: EnvelopeIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: paths.authenticated.inboundEmails,
        search: { inboundEmailId: id },
      }),
  },
  [SystemEntity.REBATE_AGREEMENT]: {
    heading: 'Rebate Agreements',
    icon: HandshakeIcon,
    navigateTo: (navigate, { id }) =>
      navigate({
        to: '/rebates/agreements/$id',
        params: { id },
      }),
  },
};

const CATEGORIES = [
  SystemEntity.TASK,
  SystemEntity.CORE_COMPANY,
  SystemEntity.AR_CUSTOMER,
  SystemEntity.AR_COLLECTION_THREAD,
  SystemEntity.AR_INVOICE,
  SystemEntity.AR_REMITTANCE_ADVICE,
  SystemEntity.AR_CASH_RECEIPT,
  SystemEntity.AR_SALES_ORDER,
  SystemEntity.AR_STAGED_SALES_ORDER,
  SystemEntity.AP_INVOICE,
  SystemEntity.INTERNAL_PURCHASE_ORDER,
  SystemEntity.PAYMENT,
  SystemEntity.AR_SAVED_REPORT,
  SystemEntity.REBATE_AGREEMENT,
  SystemEntity.WORKFLOW_DEFINITION,
  SystemEntity.INBOUND_EMAIL,
] as const;

function CategoryHeading({
  label,
  count,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const Caret = collapsed ? CaretRightIcon : CaretDownIcon;
  return (
    <button
      type="button"
      className="flex w-full items-center gap-1.5"
      onClick={onToggle}
    >
      <Caret className="size-3 shrink-0" />
      <span>{label}</span>
      <span className="bg-muted text-muted-foreground ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium leading-none">
        {count}
      </span>
    </button>
  );
}

export function EntityQuickFind() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<
    Partial<Record<SystemEntity | 'pages' | 'settings', boolean>>
  >({});
  const debouncedSearch = useDebounce(search, 200);
  const { results, isFetching } = useEntityQuickFind(debouncedSearch);
  const pageResults = useQuickSearchPages(debouncedSearch);
  const navigate = useNavigate();
  const gt = useGT();

  useConfigurableHotkey(
    HOTKEY_IDS.quickSearch,
    () => setOpen((prev) => !prev),
    { preventDefault: true },
  );

  const { label: shortcutLabel } = useHotkeyLabel(HOTKEY_IDS.quickSearch);

  const toggleCategory = useCallback((category: SystemEntity) => {
    setCollapsed((prev) => ({ ...prev, [category]: !prev[category] }));
  }, []);

  const handleSelect = useCallback(
    (item: EntityQuickFindResultItem) => {
      setOpen(false);
      setSearch('');
      const config = CATEGORY_CONFIG[item.category];
      config?.navigateTo(navigate, item);
    },
    [navigate],
  );

  const handlePageSelect = useCallback(
    (page: QuickSearchPage) => {
      setOpen(false);
      setSearch('');
      navigate({ to: page.path });
    },
    [navigate],
  );

  const matchingPages = useMemo(
    () => pageResults.filter((p) => p.kind === 'page'),
    [pageResults],
  );

  const matchingSettings = useMemo(
    () => pageResults.filter((p) => p.kind === 'setting'),
    [pageResults],
  );

  const resultsByCategory = useMemo(
    () =>
      CATEGORIES.reduce(
        (acc, category) => {
          acc[category] = results.filter((r) => r.category === category);
          return acc;
        },
        {} as Record<(typeof CATEGORIES)[number], EntityQuickFindResultItem[]>,
      ),
    [results],
  );

  return (
    <>
      <HelpMenuSlot order={HELP_MENU_ORDER.search}>
        <SidebarMenuButton
          onClick={() => setOpen(true)}
          className="cursor-pointer"
          aria-label={gt('Quick search', {
            context: 'sidebar help menu button aria label for quick search',
          })}
          tooltip={gt('Quick search ({shortcut})', {
            context:
              'tooltip on the collapsed sidebar Quick search menu item, includes keyboard shortcut',
            shortcut: shortcutLabel,
          })}
        >
          <MagnifyingGlassIcon />
          {/* Trailing label/shortcut block — hidden in icon-mode so the
              SidebarMenuButton can center the icon inside its 32×32 shell. */}
          <span className="flex w-full items-center justify-between gap-2 group-data-[collapsible=icon]:hidden">
            <span className="flex items-center gap-1.5">
              <T context="sidebar help menu item label for opening the quick search dialog">
                Quick search
              </T>
              <span className="rounded bg-amber-500/15 px-1.5 py-0.5 font-mono text-[9px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400">
                <T context="Beta badge on quick search sidebar menu item">
                  Beta
                </T>
              </span>
            </span>
            <kbd className="rounded border border-sidebar-border/60 bg-sidebar-accent/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              {shortcutLabel}
            </kbd>
          </span>
        </SidebarMenuButton>
      </HelpMenuSlot>

      <CommandDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearch('');
        }}
        title={gt('Quick Find', {
          context: 'title of the quick search dialog',
        })}
        description={gt(
          'Search pages, settings, and records across the system',
          { context: 'description of the quick search dialog' },
        )}
        showCloseButton={false}
        // Every group is already filtered upstream (entities by the backend
        // search, pages/settings by useQuickSearchPages). cmdk's own filter
        // would re-score items against their `value` string and hide results
        // matched on fields the value doesn't carry — e.g. an invoice found
        // by AMOUNT (value holds the invoice number) or a remittance found by
        // its linked payment reference (value holds the customer name).
        shouldFilter={false}
        className="sm:max-w-2xl"
      >
        <CommandInput
          placeholder={gt('Search pages, settings, and records...', {
            context: 'placeholder for quick search input',
          })}
          value={search}
          onValueChange={setSearch}
        />
        <CommandList className="max-h-[min(600px,calc(75vh-3rem))]">
          {/* Empty state keys off the LIVE input; everything below keys off the
              debounced query. With cmdk's own filtering disabled, results from
              the previous query would otherwise stay visible (and selectable)
              during the debounce window — show the loading state instead while
              the input is ahead of the debounced query. */}
          {search.trim().length === 0 ? (
            <CommandEmpty>
              {gt('Start typing to search...', {
                context: 'empty state before user types in quick search',
              })}
            </CommandEmpty>
          ) : search.trim() !== debouncedSearch.trim() ? (
            <div className="flex items-center justify-center py-6">
              <Spinner className="size-4" />
            </div>
          ) : isFetching &&
            matchingPages.length === 0 &&
            matchingSettings.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Spinner className="size-4" />
            </div>
          ) : results.length === 0 &&
            matchingPages.length === 0 &&
            matchingSettings.length === 0 ? (
            <CommandEmpty>
              {gt('No results found.', {
                context: 'empty state when quick search returns no results',
              })}
            </CommandEmpty>
          ) : (
            <>
              {matchingPages.length > 0 && (
                <CommandGroup
                  heading={
                    <CategoryHeading
                      label="Pages"
                      count={matchingPages.length}
                      collapsed={collapsed.pages ?? false}
                      onToggle={() =>
                        setCollapsed((prev) => ({
                          ...prev,
                          pages: !prev.pages,
                        }))
                      }
                    />
                  }
                >
                  {matchingPages.map((page) => (
                    <CommandItem
                      key={page.id}
                      value={`page-${page.id}-${page.label}`}
                      onSelect={() => handlePageSelect(page)}
                      className={collapsed.pages ? 'hidden' : undefined}
                    >
                      <BrowsersIcon className="size-4 shrink-0" />
                      <span>{page.label}</span>
                      {page.sublabel && (
                        <span className="text-muted-foreground ml-auto text-xs truncate">
                          {page.sublabel}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {matchingSettings.length > 0 && (
                <CommandGroup
                  heading={
                    <CategoryHeading
                      label="Settings"
                      count={matchingSettings.length}
                      collapsed={collapsed.settings ?? false}
                      onToggle={() =>
                        setCollapsed((prev) => ({
                          ...prev,
                          settings: !prev.settings,
                        }))
                      }
                    />
                  }
                >
                  {matchingSettings.map((page) => (
                    <CommandItem
                      key={page.id}
                      value={`setting-${page.id}-${page.label}`}
                      onSelect={() => handlePageSelect(page)}
                      className={collapsed.settings ? 'hidden' : undefined}
                    >
                      <GearIcon className="size-4 shrink-0" />
                      <span>{page.label}</span>
                      {page.sublabel && (
                        <span className="text-muted-foreground ml-auto text-xs truncate">
                          {page.sublabel}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {CATEGORIES.map((category) => {
                const config = CATEGORY_CONFIG[category];
                if (!config) return null;
                const items = resultsByCategory[category];
                if (items.length === 0) return null;
                const isCollapsed = collapsed[category] ?? false;
                const CategoryIcon = config.icon;
                return (
                  <CommandGroup
                    key={category}
                    heading={
                      <CategoryHeading
                        label={config.heading}
                        count={items.length}
                        collapsed={isCollapsed}
                        onToggle={() => toggleCategory(category)}
                      />
                    }
                  >
                    {items.map((item) => (
                      <CommandItem
                        key={item.id}
                        value={`${item.category}-${item.id}-${item.label}`}
                        onSelect={() => handleSelect(item)}
                        className={isCollapsed ? 'hidden' : undefined}
                      >
                        <CategoryIcon className="size-4 shrink-0" />
                        <span>{item.label}</span>
                        {item.sublabel && (
                          <span className="text-muted-foreground ml-auto text-xs truncate">
                            {item.sublabel}
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
